/**
 * REPL-style CodeMirror for the AI chat panel (Level 1).
 * Renders the full conversation as a single CM document with `# You:` and
 * `# AI:` prefixed labels. The user types at the bottom; everything above
 * is read-only. Shares syntax highlighting, autocomplete, and theming with
 * the main editor and InlineCodeMirror.
 * @module ChatCodeMirror
 * @category Chat
 */

import {
	acceptCompletion,
	autocompletion,
	closeCompletion,
	moveCompletionSelection,
} from "@codemirror/autocomplete";
import {
	history,
	historyKeymap,
	indentWithTab,
	standardKeymap,
} from "@codemirror/commands";
import { syntaxHighlighting } from "@codemirror/language";
import { EditorState, Prec, Transaction } from "@codemirror/state";
import { EditorView, highlightActiveLineGutter, keymap } from "@codemirror/view";
import type { SDK_AiChatMessageInput } from "@quickpoint.me/shared";
import {
	TextDecorations,
	getFilesByType,
	getLocale,
	text,
} from "@quickpoint.me/shared";
import {
	QPTheme,
	QpHighlightStyle,
	currentLineGutter,
	pageLines,
	pageNumbersGutter,
	rYamlParser,
} from "@quickpoint.me/shared/editor";
import { useEffect, useRef } from "react";
import { useRecoilCallback, useRecoilValue } from "recoil";
import { useAiStream } from "../../hooks/useAiStream";
import { useMetrics } from "../../hooks/useMetrics";
import { useSaveStack } from "../../hooks/useSaveStack";
import { useStartTour } from "../../hooks/useStartTour";
import {
	isTourSection,
	tourSectionStartStep,
} from "../../lib/tour/variants/accessibility/sections";
import { rYamlCompletions } from "../../lib/editor/autoComplete";
import {
	CANCEL_STREAM_EVENT,
	CREATE_MODE_EVENT,
	TOUR_JUMP_EVENT,
	chatMarkdownDecorations,
	chatRoleDecorations,
	chatRoleTheme,
	cursorBoundaryFilter,
	programmaticAnnotation,
	readOnlyBoundaryEffect,
	readOnlyBoundaryField,
	readOnlyFilter,
} from "../../lib/editor/chatReadOnlyExtension";
import { wrapSelection } from "../../lib/editor/helperPanel";
import {
	appVarsEffect,
	appVarsStateField,
	audioFilesEffect,
	audioFilesStateField,
	backLinksEffect,
	backLinksStateField,
	dataFilesEffect,
	dataFilesStateField,
	defaultFilterColors,
	filterColorsEffect,
	filterColorsStateField,
	filterNamesEffect,
	filterNamesStateField,
	imageFilesEffect,
	imageFilesStateField,
	loadedFilterFontsStateField,
} from "../../lib/editor/recoilStatesForCodeMirror";
import {
	AiChatHistoryAtom,
	AiIsStreamingAtom,
	type ChatMessage,
} from "../../state/aiChatAtoms";
import {
	STREAM_AI_EVENT,
	type StreamAiDetail,
	startAiStreaming,
} from "../../lib/tour/variants/ai-first/helpers";
import { CurrentFilterColorsAtom, EditorIsClean } from "../../state/atoms";
import { CodeMirrorCodeState } from "../../state/codeAtom";
import { AllFiles, AvailableImagesSelector } from "../../state/files";
import { ActiveFilterSelector, FilterNamesSelector } from "../../state/filters";
import { StackCfKeyAtom } from "../../state/stackAtoms";
import { BackLinksSelector, StackId } from "../../state/stackSelectors";
import { ChatCodeMirrorContainer, HumanHelpHint } from "./styled/ChatStyled";

// ── Helpers ────────────────────────────────────────────────────────────

const INPUT_PROMPT = "● You: ";

function serializeMessages(messages: ChatMessage[]): string {
	if (messages.length === 0) return "";
	return messages
		.map((m) => {
			const label =
				m.role === "user"
					? text.chat.you
					: m.role === "human"
						? text.chat.human
						: text.chat.ai;
			const content =
				m.role === "assistant" && m.isStreaming && !m.content
					? text.chat.thinking
					: m.content;
			return `● ${label}:\n${content}`;
		})
		.join("\n\n");
}

// ── Component ──────────────────────────────────────────────────────────

function ChatCodeMirror() {
	const domRef = useRef<HTMLDivElement | null>(null);
	const viewRef = useRef<EditorView | null>(null);

	// Refs for values accessed inside CM keymaps (avoids stale closures)
	const isStreamingRef = useRef(false);
	const sendRef = useRef<(input: string, createMode?: boolean) => void>(() => {});

	// ── Recoil state ───────────────────────────────────────────────────
	const stackCfKey = useRecoilValue(StackCfKeyAtom);
	const messages = useRecoilValue(AiChatHistoryAtom(stackCfKey));
	const isStreaming = useRecoilValue(AiIsStreamingAtom);
	const code = useRecoilValue(CodeMirrorCodeState(stackCfKey));
	const filter = useRecoilValue(ActiveFilterSelector);
	const availableImages = useRecoilValue(AvailableImagesSelector);
	const chatHistory = useRecoilValue(AiChatHistoryAtom(stackCfKey));
	const { sendMessage, cancelStream } = useAiStream();

	// Autocomplete data
	const allFiles = useRecoilValue(AllFiles);
	const backLinks = useRecoilValue(BackLinksSelector);
	const filterNames = useRecoilValue(FilterNamesSelector);
	const filterColors = useRecoilValue(CurrentFilterColorsAtom);
	const stackId = useRecoilValue(StackId);

	const imageFiles = getFilesByType("image", allFiles);
	const dataFiles = getFilesByType("data", allFiles);
	const audioFiles = getFilesByType("audio", allFiles);

	// Keep refs in sync
	isStreamingRef.current = isStreaming;

	sendRef.current = (userInput: string, createMode = false) => {
		const history: SDK_AiChatMessageInput[] = chatHistory
			.filter((m) => !m.isStreaming)
			.map((m) => ({ role: m.role, content: m.content }));

		sendMessage(
			{
				prompt: userInput,
				code,
				filter: filter ? JSON.stringify(filter) : undefined,
				availableImages: availableImages
					? JSON.stringify(availableImages)
					: undefined,
				chatHistory: history.length > 0 ? history : undefined,
				createMode,
				locale: getLocale(),
			},
			userInput,
		);
	};

	// ── Create CM on mount ─────────────────────────────────────────────
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs once on mount
	useEffect(() => {
		if (domRef.current && !viewRef.current) {
			const initialDoc = INPUT_PROMPT;

			const state = EditorState.create({
				doc: initialDoc,
				selection: { anchor: initialDoc.length },
				extensions: [
					keymap.of([
						...standardKeymap,
						...historyKeymap,
						indentWithTab,
					]),
					Prec.highest(
						keymap.of([
							{
								key: "Enter",
								run: (view) => {
									// Accept autocomplete first if open
									if (acceptCompletion(view)) return true;

									const boundary = view.state.field(readOnlyBoundaryField);
									const doc = view.state.doc.toString();
									const userInput = doc.slice(boundary).trim();

									if (!userInput || isStreamingRef.current) return true;

									// Clear the input area before sending so the sync
									// effect doesn't re-append it alongside the history.
									view.dispatch({
										changes: {
											from: boundary,
											to: doc.length,
											insert: "\n",
										},
									});

									sendRef.current(userInput);
									return true;
								},
							},
							{ key: "Escape", run: closeCompletion },
							{ key: "ArrowDown", run: moveCompletionSelection(true) },
							{ key: "ArrowUp", run: moveCompletionSelection(false) },
							{
								key: "Mod-b",
								run: (view) => {
									wrapSelection(TextDecorations.bold, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-i",
								run: (view) => {
									wrapSelection(TextDecorations.italic, view);
									return true;
								},
								preventDefault: true,
							},
							{
								key: "Mod-u",
								run: (view) => {
									wrapSelection(TextDecorations.underline, view);
									return true;
								},
								preventDefault: true,
							},
						]),
					),
					rYamlParser(rYamlCompletions),
					autocompletion({ closeOnBlur: false }),
					// State fields for autocomplete data
					audioFilesStateField,
					appVarsStateField,
					backLinksStateField,
					dataFilesStateField,
					filterColorsStateField.init(() => defaultFilterColors),
					filterNamesStateField,
					imageFilesStateField,
					loadedFilterFontsStateField,
					// Read-only boundary: protect "# You: " including the trailing space
					readOnlyBoundaryField.init(() => initialDoc.length),
					readOnlyFilter,
					cursorBoundaryFilter,
					chatRoleDecorations,
					chatMarkdownDecorations,
					chatRoleTheme,
					// Gutters (same as main CodeMirror)
					pageLines(),
					pageNumbersGutter,
					currentLineGutter,
					highlightActiveLineGutter(),
					// Visuals
					EditorView.lineWrapping,
					EditorView.contentAttributes.of({
						"aria-label": text.chat.chatLabel,
						spellcheck: "true",
						autocapitalize: "on",
					}),
					history(),
					QPTheme,
					syntaxHighlighting(QpHighlightStyle, { fallback: true }),
				],
			});

			const view = new EditorView({
				state,
				parent: domRef.current,
			});
			viewRef.current = view;
		}

		return () => {
			if (viewRef.current) {
				viewRef.current.destroy();
				viewRef.current = null;
			}
		};
	}, []);

	// ── Sync messages → CM document ────────────────────────────────────
	useEffect(() => {
		const view = viewRef.current;
		if (!view) return;

		const serialized = serializeMessages(messages);
		const lastMsg = messages[messages.length - 1];
		const aiIsThinking = lastMsg?.role === "assistant" && lastMsg.isStreaming;
		const newReadOnlySection = aiIsThinking
			? `${serialized}\n`
			: (serialized ? `${serialized}\n\n` : "") + INPUT_PROMPT;
		// Boundary at the end of the prompt so user types after the space
		const newBoundary = newReadOnlySection.length;

		const currentDoc = view.state.doc.toString();
		const oldBoundary = view.state.field(readOnlyBoundaryField);
		// Compare the read-only section
		const currentReadOnly = currentDoc.slice(0, oldBoundary);
		const expectedReadOnly = newReadOnlySection;

		if (currentReadOnly !== expectedReadOnly) {
			// Preserve the user's in-progress input (starts at the \n after boundary)
			const userInput = currentDoc.slice(oldBoundary);

			view.dispatch({
				annotations: [
					programmaticAnnotation.of(true),
					Transaction.addToHistory.of(false),
				],
				changes: {
					from: 0,
					to: currentDoc.length,
					insert: newReadOnlySection + userInput,
				},
				effects: readOnlyBoundaryEffect.of(newBoundary),
			});

			// Announce to screen readers
			const lastMsg = messages[messages.length - 1];
			if (lastMsg) {
				let announcement = "";
				if (lastMsg.role === "user") {
					announcement = text.chat.messageSent;
				} else if (lastMsg.isStreaming && !lastMsg.content) {
					announcement = text.chat.aiResponding;
				} else if (!lastMsg.isStreaming) {
					const summary = lastMsg.content.slice(0, 100);
					announcement = `${text.chat.ai}: ${summary}`;
				}
				if (announcement) {
					view.dispatch({
						effects: EditorView.announce.of(announcement),
					});
				}
			}

			// Place cursor right after "# You: "
			const cursorPos = view.state.field(readOnlyBoundaryField);
			view.dispatch({
				selection: { anchor: Math.min(cursorPos, view.state.doc.length) },
				scrollIntoView: true,
			});
		}
	}, [messages]);

	// ── Populate autocomplete state fields ─────────────────────────────
	useEffect(() => {
		const view = viewRef.current;
		if (!view) return;
		view.dispatch({
			effects: [
				imageFilesEffect.of(imageFiles),
				audioFilesEffect.of(audioFiles),
				dataFilesEffect.of(dataFiles),
				filterColorsEffect.of(filterColors),
				filterNamesEffect.of(filterNames),
				appVarsEffect.of({ stackCfKey: stackCfKey || false, stackId }),
				backLinksEffect.of(backLinks),
			],
		});
	}, [imageFiles, audioFiles, dataFiles, filterColors, filterNames, stackCfKey, stackId, backLinks]);

	// ── Handle cancel announcement ─────────────────────────────────────
	const prevStreamingRef = useRef(isStreaming);
	useEffect(() => {
		if (prevStreamingRef.current && !isStreaming && viewRef.current) {
			// Streaming just ended — if it was a cancel (no new non-streaming assistant msg),
			// the sync effect handles the doc update. Just check for cancel case.
			const lastMsg = messages[messages.length - 1];
			if (!lastMsg || lastMsg.role === "user") {
				viewRef.current.dispatch({
					effects: EditorView.announce.of(text.chat.generationCancelled),
				});
			}
		}
		prevStreamingRef.current = isStreaming;
	}, [isStreaming, messages]);

	// ── Handle Create button click from CodeMirror widget ──────────────
	useEffect(() => {
		const handleCreateMode = () => {
			if (isStreamingRef.current) return;
			// Send with createMode=true to instruct AI to generate the presentation
			sendRef.current(text.chat.createPrompt, true);
		};

		window.addEventListener(CREATE_MODE_EVENT, handleCreateMode);
		return () => window.removeEventListener(CREATE_MODE_EVENT, handleCreateMode);
	}, []);

	// ── Handle Cancel button click from CodeMirror widget ───────────────
	useEffect(() => {
		const handleCancel = () => {
			cancelStream();
		};

		window.addEventListener(CANCEL_STREAM_EVENT, handleCancel);
		return () => window.removeEventListener(CANCEL_STREAM_EVENT, handleCancel);
	}, [cancelStream]);

	// ── Handle tour link click from CodeMirror widget ───────────────────
	// The tour runs on the demo stack, so the user's presentation only
	// needs saving — it is never wiped by the demo steps.
	const editorIsClean = useRecoilValue(EditorIsClean);
	const save = useSaveStack();
	const startTour = useStartTour();
	const { trackFeature } = useMetrics();

	const tourJumpRef = useRef<(section: string) => void>(() => {});
	tourJumpRef.current = (section: string) => {
		if (!isTourSection(section)) return;
		if (!editorIsClean) save();
		trackFeature("ai_tour_link");
		startTour("accessibility", tourSectionStartStep(section));
	};

	useEffect(() => {
		const handleTourJump = (e: Event) => {
			const { section } = (e as CustomEvent<{ section: string }>).detail;
			tourJumpRef.current(section);
		};

		window.addEventListener(TOUR_JUMP_EVENT, handleTourJump);
		return () => window.removeEventListener(TOUR_JUMP_EVENT, handleTourJump);
	}, []);

	// ── Handle mock AI streaming from tour demo ────────────────────────
	const setChatHistory = useRecoilCallback(
		({ set }) =>
			(msgs: ChatMessage[]) => {
				set(AiChatHistoryAtom(stackCfKey), msgs);
			},
		[stackCfKey],
	);

	useEffect(() => {
		let cleanup: (() => void) | undefined;

		const handler = (e: Event) => {
			const { userPrompt, aiResponse } = (e as CustomEvent<StreamAiDetail>).detail;
			const now = Date.now();
			const userMsg: ChatMessage = {
				id: `tour-user-${now}`,
				role: "user",
				content: userPrompt,
				timestamp: now,
			};
			const assistantId = `tour-ai-${now}`;

			// Clear the typed prompt from the CM editable area
			const view = viewRef.current;
			if (view) {
				const boundary = view.state.field(readOnlyBoundaryField);
				const docLen = view.state.doc.length;
				if (docLen > boundary) {
					view.dispatch({
						changes: { from: boundary, to: docLen, insert: "" },
					});
				}
			}

			// Add user message + empty streaming assistant message
			setChatHistory([
				userMsg,
				{ id: assistantId, role: "assistant", content: "", timestamp: now, isStreaming: true },
			]);

			// Stream AI response character by character
			cleanup = startAiStreaming(
				aiResponse,
				(textSoFar) => {
					setChatHistory([
						userMsg,
						{ id: assistantId, role: "assistant", content: textSoFar, timestamp: now, isStreaming: true },
					]);
				},
				() => {
					setChatHistory([
						userMsg,
						{ id: assistantId, role: "assistant", content: aiResponse, timestamp: now, isStreaming: false },
					]);
				},
			);
		};

		window.addEventListener(STREAM_AI_EVENT, handler);
		return () => {
			window.removeEventListener(STREAM_AI_EVENT, handler);
			cleanup?.();
		};
	}, [setChatHistory]);

	return (
		<ChatCodeMirrorContainer>
			<HumanHelpHint>{text.chat.humanHelpHint}</HumanHelpHint>
			<div ref={domRef} className="chat-codemirror" />
		</ChatCodeMirrorContainer>
	);
}

export default ChatCodeMirror;
